/**
 * Tier insignias for UNVEILED memberships.
 * - Silver shield-style sigil for Verified Witness ($15/mo)
 * - Gold circular emblem for Research Council ($49/mo)
 * Sizes: sm (16), md (24), lg (40), xl (64)
 */

const SIZE_PX = { sm: 16, md: 24, lg: 40, xl: 64 };

export function VerifiedWitnessBadge({ size = "md", className = "", title = "Verified Witness" }) {
    const s = SIZE_PX[size] || SIZE_PX.md;
    return (
        <svg viewBox="0 0 64 64" width={s} height={s} className={`inline-block align-middle ${className}`} role="img" aria-label={title} data-testid="badge-verified-witness">
            <title>{title}</title>
            <defs>
                <linearGradient id="vw-silver" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f5f6f7" />
                    <stop offset="40%" stopColor="#c2c6cb" />
                    <stop offset="100%" stopColor="#6e7479" />
                </linearGradient>
                <radialGradient id="vw-glint" cx="0.3" cy="0.25" r="0.6">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.55" />
                    <stop offset="60%" stopColor="#ffffff" stopOpacity="0" />
                </radialGradient>
            </defs>
            {/* Shield silhouette */}
            <path d="M32 4 L56 13 V32 C56 46 44 56 32 60 C20 56 8 46 8 32 V13 Z" fill="url(#vw-silver)" stroke="#3c4044" strokeWidth="1" />
            <path d="M32 4 L56 13 V32 C56 46 44 56 32 60 C20 56 8 46 8 32 V13 Z" fill="url(#vw-glint)" />
            {/* Inner ring */}
            <path d="M32 10 L50 17 V32 C50 43 41 51 32 54 C23 51 14 43 14 32 V17 Z" fill="none" stroke="#3c4044" strokeWidth="0.7" opacity="0.6" />
            {/* Eye-witness motif: open eye */}
            <ellipse cx="32" cy="30" rx="11" ry="6" fill="none" stroke="#26282b" strokeWidth="1.6" />
            <circle cx="32" cy="30" r="3.6" fill="#26282b" />
            <circle cx="33.2" cy="28.8" r="1.1" fill="#f5f6f7" />
            {/* Banner */}
            <rect x="14" y="40" width="36" height="9" fill="#26282b" stroke="#3c4044" strokeWidth="0.6" rx="1" />
            <text x="32" y="46.5" textAnchor="middle" fontFamily="Georgia, serif" fontSize="5" fill="#dfe2e4" letterSpacing="0.5">WITNESS</text>
        </svg>
    );
}

export function ResearchCouncilBadge({ size = "md", className = "", title = "Research Council · Trusted Source" }) {
    const s = SIZE_PX[size] || SIZE_PX.md;
    return (
        <svg viewBox="0 0 80 80" width={s} height={s} className={`inline-block align-middle ${className}`} role="img" aria-label={title} data-testid="badge-research-council">
            <title>{title}</title>
            <defs>
                <radialGradient id="rc-gold" cx="0.35" cy="0.3" r="0.85">
                    <stop offset="0%" stopColor="#ffe8a3" />
                    <stop offset="35%" stopColor="#e7c873" />
                    <stop offset="70%" stopColor="#b18a2e" />
                    <stop offset="100%" stopColor="#5e4615" />
                </radialGradient>
                <linearGradient id="rc-rim" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#fff1be" />
                    <stop offset="100%" stopColor="#7a5d22" />
                </linearGradient>
                <path id="rc-arc-top" d="M 12 40 A 28 28 0 0 1 68 40" />
                <path id="rc-arc-bot" d="M 14 44 A 26 26 0 0 0 66 44" />
            </defs>
            {/* Outer rim */}
            <circle cx="40" cy="40" r="38" fill="url(#rc-rim)" />
            <circle cx="40" cy="40" r="36" fill="url(#rc-gold)" stroke="#3c2e10" strokeWidth="0.8" />
            {/* Inscribed text on the ring */}
            <g fill="#2a1f08" fontFamily="Georgia, serif" fontWeight="700">
                <text fontSize="6.2" letterSpacing="1.5">
                    <textPath href="#rc-arc-top" startOffset="50%" textAnchor="middle">RESEARCH · COUNCIL</textPath>
                </text>
                <text fontSize="4.5" letterSpacing="1.2">
                    <textPath href="#rc-arc-bot" startOffset="50%" textAnchor="middle">TRUSTED · SOURCE</textPath>
                </text>
            </g>
            {/* Inner medallion */}
            <circle cx="40" cy="40" r="22" fill="#1a140a" stroke="#7a5d22" strokeWidth="1.2" />
            <circle cx="40" cy="40" r="20" fill="none" stroke="#e7c873" strokeWidth="0.4" opacity="0.7" />
            {/* Central glyph — compass / archive sigil */}
            <g transform="translate(40 40)">
                <polygon points="0,-15 4,0 0,15 -4,0" fill="#e7c873" />
                <polygon points="-15,0 0,-4 15,0 0,4" fill="#b18a2e" />
                <circle r="3" fill="#1a140a" stroke="#e7c873" strokeWidth="1" />
                <circle r="1.2" fill="#e7c873" />
            </g>
            {/* Two stars marking start/end of ring text */}
            <g fill="#2a1f08">
                <polygon points="40,8 41,11 44,11 41.5,13 42.5,16 40,14 37.5,16 38.5,13 36,11 39,11" transform="translate(-20 0) scale(0.7) translate(28.5 -3)" />
                <polygon points="40,8 41,11 44,11 41.5,13 42.5,16 40,14 37.5,16 38.5,13 36,11 39,11" transform="translate(20 0) scale(0.7) translate(28.5 -3)" />
            </g>
        </svg>
    );
}

export function MembershipBadge({ tier, size = "md", className = "" }) {
    if (tier === "research_council") return <ResearchCouncilBadge size={size} className={className} />;
    if (tier === "verified_witness") return <VerifiedWitnessBadge size={size} className={className} />;
    return null;
}
